import type Button from "../../../src/models/Button";
import type Consumer from "../../../src/models/Consumer";
import { ChannelType } from "../../../src/models/ChannelType";
import { classifyConsumerDevice } from "../../../src/models/ConsumerDeviceKind";
import { C1_EloItem_PlugType } from "../types/C1_EloTypes";
import { getEloItemProfile } from "../data/C1_eloItemsRegistry";
import type { C1_FurnitureData } from "../types/C1_ModelTypes";
import type { C1_DefinitionItem } from "./C1_DefinitionItem";
import { C1_Button } from "./C1_Button";
import { C1_Consumer } from "./C1_Consumer";
import C1_EloItem from "./C1_EloItem";
import type { C1_Room } from "./C1_Room";
import { createEquipmentInstance } from "./C1_ensureEloEquipmentDefaults";
import { c1_state } from "./C1_runtimeContext";
import { isLightGroupDefinitionId } from "../furniture/C1_lightGroup";

export function paletteIdForExistingDevice(device: Button | Consumer): string {
  if (device.isButton) return device.isSensor ? "electricalSensor" : "electricalButton";
  const consumer = device as Consumer;
  if (consumer.c1 instanceof C1_Consumer && consumer.c1.data.lightGroupFurniture) {
    const paletteId = consumer.c1.data.interactivePlacement?.paletteId;
    if (paletteId && isLightGroupDefinitionId(paletteId)) return paletteId;
    return consumer.c1._defId.startsWith("outdoorLightGroup") ? "outdoorLightGroup" : "lightGroup";
  }
  if (consumer.channelType === ChannelType.IntermediateDistribution) return "electricalDistribution";
  const deviceKind = classifyConsumerDevice(consumer).kind;
  switch (deviceKind) {
    case "socket": return "electricalOutlet";
    case "light": return "electricalLight";
    case "camera": return "electricalCamera";
    case "jalousie": return "electricalJalousie";
    case "blind": return "electricalBlind";
    case "contact": return "electricalWindowContact";
    default: return consumer.channelType === ChannelType.suply400V ? "fixedConsumer400" : "fixedConsumer";
  }
}

/** Attaches placement to the existing owner without recreating channels, products or connections. */
export function placeExistingElectricalDevice(room: C1_Room, device: Button | Consumer, placement: C1_FurnitureData): C1_Button | C1_Consumer {
  if (!room.owner) throw new Error("The placement room has no ESTADA owner.");
  let parent = device.parent;
  while (parent && parent.className !== "Room") parent = parent.parent;
  if (parent !== room.owner) throw new Error("The device must be placed in its existing room.");
  // A light array is already one consumer. Reposition its existing layout instead
  // of replacing it with a standalone light definition and leaving an orphaned array.
  if (device.c1 instanceof C1_Consumer && device.c1.data.lightGroupFurniture) {
    const facet = device.c1;
    const layout = facet.data.lightGroupFurniture!;
    if (device.parent !== room.owner && !device.moveToNewParent(room.owner)) throw new Error("The device cannot be attached to its room.");
    facet.setLightGroupFurniture({
      ...placement, definitionId: paletteIdForExistingDevice(device),
      baseProps: { ...layout.baseProps, rotation: Number(placement.baseProps.rotation) || 0 },
    });
    facet.lightGroup!.setPosition(placement.relativePosition, placement.side, placement.reflect,
      placement.wallDocked, placement._wallId);
    return facet;
  }
  const tree = createEquipmentInstance(placement.definitionId, c1_state.project.equipmentStandard,
    getEloItemProfile(placement.definitionId), c1_state.settings.buttonDefaults);
  let source = tree?.items[0];
  let path = placement.definitionId;
  while (source?.items.length === 1) {
    path += `/${source.definitionId}`;
    source = source.items[0];
  }
  if (!source) throw new Error("Missing electrical placement definition.");
  path += `/${source.definitionId}`;
  const definition = state.objectRegistry.getObject<C1_DefinitionItem>(path);
  if (!definition) throw new Error(`Unknown electrical placement definition: ${path}`);
  const existing = device.c1 instanceof C1_EloItem ? device.c1.data : undefined;
  const data = {
    ...existing, _defId: definition.id, position: undefined, interactivCreated: true,
    details: {...source.details, ...existing?.details}, properties: existing?.properties ?? source.properties,
  };
  // The catalog default is 230 V; keep the existing socket's actual subtype instead.
  if (!device.isButton) {
    const identity = classifyConsumerDevice(device as Consumer);
    if (identity.kind === "socket") {
      data.details = {...data.details, plugType: identity.plugType as unknown as C1_EloItem_PlugType};
    }
  }
  const name = device.name;
  if (device.parent !== room.owner && !device.moveToNewParent(room.owner)) throw new Error("The device cannot be attached to its room.");
  const facet = device.isButton ? new C1_Button(data, device) : new C1_Consumer(data, device);
  device.c1 = facet;
  facet.setInteractivePlacement({paletteId: placement.definitionId, relativePosition: placement.relativePosition,
    side: placement.side, _wallId: placement._wallId, wallDocked: placement.wallDocked,
    rotation: Number(placement.baseProps.rotation) || 0});
  device.name = name;
  return facet;
}
